import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    MdEdit,
    MdEmail,
    MdPhone,
    MdBusiness,
    MdDescription,
    MdAnalytics,
    MdSwapHoriz,
    MdCalendarMonth,
    MdPerson,
    MdGroups,
    MdLocationOn,
    MdBook,
    MdFilePresent,
    MdChecklist,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime, formatUtcToLocalDate } from '../../Utils/DateTime';
import {
    getOrganizationTransitionByOrgId,
    getAllOrganizationReports,
    getAllDocumentsByOrgId,
    getOrganizationSOP,
    downloadOrganizationDocument,
    getOrganizationById,
    getOrgMemberDetails
} from '../../Services/OrganizationService';
import { getLocationsByOrgId } from '../../Services/LocationService';

import FileActions from '../../Components/Common/FileActions';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
    Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
    Inactive: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
    Pending: { backgroundColor: 'var(--hc-status-pending-alt-bg)', color: 'var(--hc-blue-700)' },
    Suspended: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' },
};

const formatAddress = (address) => {
    if (!address) {
        return '-';
    }

    const parts = [
        address.street,
        address.street2,
        address.city,
        address.state,
        address.country,
        address.zipcode,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : '-';
};

const formatDocumentDate = (value) => {
    if (!value) {
        return '-';
    }

    try {
        return formatUtcToLocalDateTime(value);
    } catch {
        return value;
    }
};

const normalizeOrganization = (org) => {
    if (!org) {
        return null;
    }

    const statusName =
        org.status_name || org.StatusName || (org.status_id === 1 ? 'Active' : 'Inactive');

    return {
        ...org,
        id: org.id ?? org.Id ?? null,
        Id: org.id ?? org.Id ?? null,
        account_name: org.account_name ?? org.AccountName ?? '',
        group_office_name: org.group_office_name ?? org.GroupOfficeName ?? org.OrganizationName ?? '',
        dba: org.dba ?? org.Dba ?? '',
        cid: org.cid ?? org.Cid ?? '',
        tax_id: org.tax_id ?? org.TaxId ?? '',
        group_npi: org.group_npi ?? org.GroupNpi ?? '',
        medicaid_ptan: org.medicaid_ptan ?? org.MedicaidPtan ?? null,
        medicaid_group: org.medicaid_group ?? org.MedicaidGroup ?? null,
        bcbs_group: org.bcbs_group ?? org.BcbsGroup ?? null,
        phone: org.phone ?? org.Phone ?? '',
        fax: org.fax ?? org.Fax ?? '',
        erx: org.erx ?? org.Erx ?? false,
        drug_injection: org.drug_injection ?? org.DrugInjection ?? false,
        drug_injection_notes: org.drug_injection_notes ?? org.DrugInjectionNotes ?? null,
        labs: org.labs ?? org.Labs ?? false,
        emr_name: org.emr_name ?? org.EmrName ?? null,
        pm: org.pm ?? org.Pm ?? null,
        clearing_house: org.clearing_house ?? org.ClearingHouse ?? null,
        pos_list: org.pos_list ?? org.PosList ?? [],
        avg_monthly_volume: org.avg_monthly_volume ?? org.AvgMonthlyVolume ?? null,
        avg_monthly_collections:
            org.avg_monthly_collections ?? org.AvgMonthlyCollections ?? null,
        mailing_address: org.mailing_address ?? org.MailingAddress ?? null,
        pay_to_address: org.pay_to_address ?? org.PayToAddress ?? null,
        taxonomies: Array.isArray(org.taxonomies) ? org.taxonomies : [],
        created_on: org.created_on ?? org.CreatedOn ?? null,
        modified_on: org.modified_on ?? org.ModifiedOn ?? null,
        created_by: org.created_by ?? org.CreatedBy ?? '',
        modified_by: org.modified_by ?? org.ModifiedBy ?? '',
        statusName,
    };
};

const normalizeDocument = (doc) => {
    if (!doc) {
        return null;
    }

    return {
        id: doc.id ?? doc.Id ?? null,
        file_id: doc.file_id ?? doc.fileId ?? doc.file_id ?? null,
        title: doc.title ?? doc.Title ?? 'Document',
        status_id: Number(doc.status_id ?? doc.StatusId ?? 1),
        status_name: doc.status_name ?? doc.StatusName ?? 'Active',
        expire_in_days: doc.expire_in_days ?? doc.expireInDays ?? null,
        effective_start_date: doc.effective_start_date ?? doc.effectiveStartDate ?? null,
        termination_date: doc.termination_date ?? doc.terminationDate ?? null,
        created_on: doc.created_on ?? doc.CreatedOn ?? null,
        file_name: doc.file_name ?? doc.fileName ?? '',
        metadata: Array.isArray(doc.metadata)
            ? doc.metadata
            : typeof doc.metadata === 'object' && doc.metadata
                ? Object.keys(doc.metadata).map((key) => ({ key, value: doc.metadata[key] }))
                : [],
        raw: doc,
    };
};

const renderFormattedIdentifier = (value, type) => {
    const input = String(value ?? '').trim();
    if (!input) {
        return '-';
    }

    const digitsOnly = input.replace(/\D/g, '');
    let formatted = input;

    const isPureDigits = /^[0-9]+$/.test(input);
    if (isPureDigits) {
        if (type === 'taxid' && digitsOnly.length === 9) {
            formatted = `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2)}`;
        } else if (type === 'npi' && digitsOnly.length === 10) {
            formatted = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
        } else if (type === 'fax') {
            if (digitsOnly.length === 10) {
                formatted = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
            } else if (digitsOnly.length === 7) {
                formatted = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
            }
        }
    }

    return formatted;
};

const OrganizationView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );

    const canEditOrganization = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.ORGANIZATIONS_LIST, decodedToken);

    const [organization, setOrganization] = React.useState(null);
    const resolvedId = React.useMemo(() => id || location.state?.organization?.Id || decodedToken?.organization_id || null, [id, location.state, decodedToken]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    const [documents, setDocuments] = React.useState([]);
    const [missingDocuments, setMissingDocuments] = React.useState([]);
    const [notApplicableDocuments, setNotApplicableDocuments] = React.useState([]);
    const [docsLoading, setDocsLoading] = React.useState(false);
    const [detailLoading, setDetailLoading] = React.useState(false);

    const [transitionInfo, setTransitionInfo] = React.useState(null);
    const [reports, setReports] = React.useState([]);
    const [sop, setSop] = React.useState(null);
    const [primaryLocation, setPrimaryLocation] = React.useState(null);
    const [orgMembers, setOrgMembers] = React.useState([]);
    const [membersLoading, setMembersLoading] = React.useState(false);

    const [activeDocumentId, setActiveDocumentId] = React.useState(null);
    const [reportPage, setReportPage] = React.useState(0);
    const [taxonomyPage, setTaxonomyPage] = React.useState(0);

    const fetchOrganizationDocument = async (token, fileId, fileName) => {
        const dl = await downloadOrganizationDocument(fileId, token);
        const payload = dl?.data ?? dl;
        const dataStr = typeof payload === 'string' ? payload : (payload?.file || payload?.data || null);
        const filename =
            payload?.file_name ||
            payload?.filename ||
            dl?.file_name ||
            dl?.filename ||
            fileName ||
            `document-${fileId}`;

        if (!dataStr || typeof dataStr !== 'string') {
            return { message: dl?.message || 'No file returned' };
        }

        let mime = 'application/octet-stream';
        let base64 = dataStr;

        if (dataStr.startsWith('data:')) {
            const parts = dataStr.split(',');
            mime = parts[0].split(':')[1].split(';')[0];
            base64 = parts[1] || '';
        } else {
            const ext = (filename.split('.').pop() || '').toLowerCase();
            if (ext === 'pdf') mime = 'application/pdf';
            else if (ext === 'doc') mime = 'application/msword';
            else if (ext === 'docx') mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            else if (ext === 'xlsx') mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            else if (ext === 'xls') mime = 'application/vnd.ms-excel';
            else if (ext === 'txt') mime = 'text/plain';
        }

        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
        return { blob: new Blob([bytes], { type: mime }), filename, contentType: mime };
    };

    React.useEffect(() => {
        if (!resolvedId || !token) {
            return;
        }

        if (organization) {
            return;
        }

        let mounted = true;
        const loadOrganization = async () => {
            setLoading(true);
            setError('');
            try {
                // Check if organization data is available in location state
                const locationOrganization = location.state?.organization;

                if (locationOrganization) {
                    if (mounted) {
                        setOrganization(normalizeOrganization(locationOrganization));
                    }
                } else {
                    // No identity to resolve to (e.g. a view-only user with no organization_id
                    // claim) -- there is no "first record" fallback anymore; show the empty
                    // state instead.
                    let targetOrgId = resolvedId && resolvedId !== 'first' && resolvedId !== ':id' ? resolvedId : null;
                    if (!targetOrgId) {
                        if (mounted) {
                            setError('No organizations available.');
                        }
                        return;
                    }
                    const response = await getOrganizationById(targetOrgId, token);
                    if (mounted && response?.is_success) {
                        setOrganization(normalizeOrganization(response.data ?? response));
                    } else if (mounted) {
                        setError(response?.message || 'Unable to load organization details.');
                    }
                }
            } catch (err) {
                console.error('Failed to load organization:', err);
                if (mounted) {
                    setError('Unable to load organization details.');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadOrganization();
        return () => {
            mounted = false;
        };
    }, [resolvedId, token, organization, location]);

    React.useEffect(() => {
        if (!organization?.id || !token) {
            return;
        }

        let mounted = true;
        const loadDetails = async () => {
            setDocsLoading(true);
            setDetailLoading(true);
            try {
                const [docResponse, transitionResponse, reportResponse, sopResponse, locationResponse, memberResponse] =
                    await Promise.all([
                        getAllDocumentsByOrgId(organization.id, token),
                        getOrganizationTransitionByOrgId(organization.id, token),
                        getAllOrganizationReports(organization.id, token),
                        getOrganizationSOP(organization.id, token),
                        getLocationsByOrgId(organization.id, token),
                        getOrgMemberDetails(organization.id, token),
                    ]);

                if (mounted) {
                    if (docResponse?.is_success) {
                        const docs = Array.isArray(docResponse.data)
                            ? docResponse.data
                            : Array.isArray(docResponse.data?.documents)
                                ? docResponse.data.documents
                                : Array.isArray(docResponse.data?.data)
                                    ? docResponse.data.data
                                    : [];
                        const missingDocs = Array.isArray(docResponse.data?.missing_documents)
                            ? docResponse.data.missing_documents
                            : [];
                        const notApplicableDocs = Array.isArray(docResponse.data?.not_applicable_documents)
                            ? docResponse.data.not_applicable_documents
                            : [];
                        const normalizedDocs = docs.map(normalizeDocument).filter(Boolean);
                        setDocuments(normalizedDocs.filter((doc) => doc.status_id === 1));
                        setMissingDocuments(missingDocs);
                        setNotApplicableDocuments(
                            notApplicableDocs
                                .map((item) =>
                                    typeof item === 'string'
                                        ? { title: item }
                                        : normalizeDocument(item)
                                )
                                .filter(Boolean)
                        );
                    } else {
                        setDocuments([]);
                        setMissingDocuments([]);
                        setNotApplicableDocuments([]);
                    }

                    if (transitionResponse?.is_success) {
                        setTransitionInfo(transitionResponse.data ?? transitionResponse);
                    }

                    if (reportResponse?.is_success) {
                        setReports(Array.isArray(reportResponse.data) ? reportResponse.data : []);
                    }

                    if (sopResponse?.is_success) {
                        setSop(sopResponse.data ?? sopResponse);
                    }

                    if (memberResponse?.is_success) {
                        setOrgMembers(Array.isArray(memberResponse.data) ? memberResponse.data : []);
                    } else {
                        setOrgMembers([]);
                    }

                    if (locationResponse?.is_success) {
                        setPrimaryLocation(locationResponse.data?.address ?? locationResponse.data ?? null);
                    } else {
                        setPrimaryLocation(null);
                    }
                }
            } catch (err) {
                console.error('Failed to load organization details:', err);
            } finally {
                if (mounted) {
                    setDocsLoading(false);
                    setDetailLoading(false);
                }
            }
        };

        loadDetails();
        return () => {
            mounted = false;
        };
    }, [organization, token]);

    const handleToggleDocument = (docId) => {
        setActiveDocumentId((current) => (current === docId ? null : docId));
    };

    const handleReportPrev = () => {
        setReportPage((prev) => Math.max(prev - 1, 0));
    };

    const handleReportNext = () => {
        setReportPage((prev) => prev + 1);
    };

    const handleTaxonomyPrev = () => {
        setTaxonomyPage((prev) => Math.max(prev - 1, 0));
    };

    const handleTaxonomyNext = () => {
        setTaxonomyPage((prev) => prev + 1);
    };

    const generalStats = React.useMemo(() => {
        return [
            {
                label: 'Documents',
                value: documents.length,
                icon: <MdDescription size={20} className="hc-text-primary" />,
                accent: 'bg-primary-subtle',
            },
            {
                label: 'Reports',
                value: reports.length,
                icon: <MdAnalytics size={20} className="hc-text-primary" />,
                accent: 'bg-info-subtle',
            },
            {
                label: 'Transition',
                value: transitionInfo ? 'Planned' : 'Not set',
                icon: <MdSwapHoriz size={20} className="hc-text-primary" />,
                accent: 'bg-warning-subtle',
            },
            {
                label: 'Taxonomies',
                value: organization?.taxonomies?.length || 0,
                icon: <MdBusiness size={20} className="hc-text-primary" />,
                accent: 'bg-secondary-subtle',
            },
        ];
    }, [documents.length, reports.length, transitionInfo, organization?.taxonomies?.length]);

    const createdOn = React.useMemo(
        () => formatDocumentDate(organization?.created_on),
        [organization?.created_on],
    );

    const modifiedOn = React.useMemo(
        () => formatDocumentDate(organization?.modified_on),
        [organization?.modified_on],
    );

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="row g-4">
                    <div className="col-12">
                        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 280, borderRadius: 24 }} />
                    </div>
                    {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="col-lg-3 col-md-6">
                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 20 }} />
                        </div>
                    ))}
                    <div className="col-lg-8">
                        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 420, borderRadius: 20 }} />
                    </div>
                    <div className="col-lg-4">
                        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 420, borderRadius: 20 }} />
                    </div>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <ViewEmptyState
                title="No Organizations Available"
                description="There are no organization records available to display."
                icon={MdBusiness}
                canViewList={canViewList}
                backRoute={APPRoutes.ORGANIZATIONS}
                backLabel="Back to Organizations"
            />
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton onClick={() => navigate(APPRoutes.ORGANIZATIONS)} />
                )}
                {canEditOrganization ? (
                    <button
                        type="button"
                        className="btn hc-btn-next border-0 d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.ORGANIZATIONS_EDIT.replace(':id', organization.id))}
                    >
                        <MdEdit size={18} />
                        Edit Organization
                    </button>
                ) : null}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                            <span
                                className="px-3 py-2 rounded-pill fw-semibold"
                                style={{
                                    fontSize: '0.82rem',
                                    backgroundColor: STATUS_STYLE[organization.statusName]?.backgroundColor || 'var(--hc-status-neutral-bg)',
                                    color: STATUS_STYLE[organization.statusName]?.color || 'var(--hc-table-footer-text)',
                                }}
                            >
                                {organization.statusName}
                            </span>
                            {organization.cid ? (
                                <span
                                    className="px-3 py-2 rounded-pill"
                                    style={{
                                        fontSize: '0.82rem',
                                        backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)',
                                        color: 'var(--hc-white)',
                                    }}
                                >
                                    {organization.cid}
                                </span>
                            ) : null}
                        </div>

                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {organization.group_office_name || organization.account_name}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
                            <MdBusiness size={14} className="me-1" />{organization.dba || 'No DBA provided'}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)', fontSize: '0.88rem' }}>
                            <MdDescription size={14} className="me-1" />{organization.taxonomies?.length || 0} taxonomies assigned
                        </p>
                    </div>

                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div
                                    className="d-flex align-items-center justify-content-center overflow-hidden rounded-3 flex-shrink-0"
                                    style={{ width: 56, height: 56, background: 'rgba(var(--hc-white-rgb), 0.15)' }}
                                >
                                    <MdBusiness size={28} className="text-white" />
                                </div>
                                <div>
                                    <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                        Organization Overview
                                    </p>
                                    <p className="fw-bold text-white mb-0" style={{ fontSize: '1.55rem', lineHeight: 1.1 }}>
                                        {organization.taxonomies?.length || 0}
                                    </p>
                                </div>
                            </div>
                            <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Rich organization profile, document audit, and transition timelines.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                {generalStats.map((stat) => (
                    <div key={stat.label} className="col-sm-6 col-lg-3">
                        <div className="p-4 rounded-4 hc-surface hc-card-primary h-100 d-flex align-items-center gap-3 shadow-sm">
                            <div
                                className={`d-inline-flex align-items-center justify-content-center rounded-3 ${stat.accent}`}
                                style={{ width: 44, height: 44 }}
                            >
                                {stat.icon}
                            </div>
                            <div>
                                <div className="small text-muted mb-1">{stat.label}</div>
                                <div className="fw-bold fs-5">{stat.value}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-4 mb-4 align-items-stretch">
                <div className="col-xl-8 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary mb-4 h-100 w-100">
                        <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
                            <MdBusiness size={24} className="hc-text-primary" /> Organization Details
                        </h5>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Organization Name</div>
                                    <div className="fw-semibold text-dark">{organization.group_office_name || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">CID</div>
                                    <div className="fw-semibold text-dark">{organization.cid || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Client Name</div>
                                    <div className="fw-semibold text-dark">{organization.account_name || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">DBA</div>
                                    <div className="fw-semibold text-dark">{organization.dba || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Tax ID</div>
                                    <div className="fw-semibold text-dark">
                                        {renderFormattedIdentifier(organization.tax_id, 'taxid')}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Group NPI#</div>
                                    <div className="fw-semibold text-dark">
                                        {renderFormattedIdentifier(organization.group_npi, 'npi')}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Primary Phone#</div>
                                    <div className="fw-semibold text-dark">{organization.phone || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                    <div className="small hc-text-primary fw-semibold mb-1">Primary Fax#</div>
                                    <div className="fw-semibold text-dark">
                                        {renderFormattedIdentifier(organization.fax, 'fax')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-4 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <MdSwapHoriz size={24} className="hc-text-primary" />
                            <h5 className="fw-bold mb-0 hc-text-primary">Transition Timeline</h5>
                        </div>
                        {detailLoading ? (
                            <div className="row g-3">
                                {[1, 2, 3, 4].map((item) => (
                                    <div key={item} className="col-12">
                                        <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                            <div className="skeleton mb-2 rounded-3" style={{ height: 16, width: '30%' }} />
                                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 20, width: '65%', borderRadius: 8 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="row g-3">
                                <div className="col-12">
                                    <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                        <div className="small hc-text-primary fw-semibold mb-1">Transition Date</div>
                                        <div className="fw-semibold text-dark">
                                            {transitionInfo?.transitioned_date ? formatUtcToLocalDate(transitionInfo.transitioned_date) : '-'}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                        <div className="small hc-text-primary fw-semibold mb-1">Start Date</div>
                                        <div className="fw-semibold text-dark">
                                            {transitionInfo?.start_date ? formatUtcToLocalDate(transitionInfo.start_date) : '-'}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                        <div className="small hc-text-primary mb-1">Bill from DOS</div>
                                        <div className="fw-semibold text-dark">
                                            {transitionInfo?.bill_from_dos ? formatUtcToLocalDate(transitionInfo.bill_from_dos) : '-'}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                        <div className="small hc-text-primary fw-semibold">Notes</div>
                                        <div className="text-secondary" style={{ minHeight: 48 }}>
                                            {transitionInfo?.notes || 'No transition notes available.'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4 align-items-stretch">
                <div className="col-xl-8 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                        <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
                            <MdLocationOn size={24} className="hc-text-primary" /> Addresses
                        </h5>
                        <div className="row g-3">
                            <div className="col-12">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Primary Location</div>
                                    {detailLoading ? (
                                        <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 20, width: '70%', borderRadius: 8 }} />
                                    ) : (
                                        <div className="fw-semibold text-dark">{formatAddress(primaryLocation)}</div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Mailing Address</div>
                                    <div className="fw-semibold text-dark">{formatAddress(organization.mailing_address)}</div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Pay-to Address</div>
                                    <div className="fw-semibold text-dark">{formatAddress(organization.pay_to_address)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-4 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <MdBook size={24} className="hc-text-primary" />
                            <h5 className="fw-bold mb-0 hc-text-primary">Taxonomies</h5>
                        </div>
                        {detailLoading ? (
                            <div className="row g-3">
                                {Array.from({ length: 2 }).map((_, index) => (
                                    <div key={index} className="col-12">
                                        <div className="rounded-4 border p-4 bg-light-subtle h-100">
                                            <div className="skeleton mb-3 rounded-3" style={{ height: 16, width: '45%' }} />
                                            <div className="skeleton mb-2 rounded-3" style={{ height: 18, width: '65%' }} />
                                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 14, width: '40%', borderRadius: 8 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : organization.taxonomies?.length ? (
                            <>
                                <div className="row g-3">
                                    {organization.taxonomies
                                        .slice(taxonomyPage * 2, taxonomyPage * 2 + 2)
                                        .map((tax) => (
                                            <div key={tax.id} className="col-12">
                                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                                        <div className="small text-muted">{tax.taxonomy_group || tax.description}</div>
                                                        {tax.is_custom && <span className="badge rounded-pill hc-bg-primary text-white">Custom</span>}
                                                    </div>
                                                    <div className="fw-semibold text-dark">{tax.code || '-'}</div>
                                                    <div className="small text-secondary mt-1">{tax.state || 'N/A'}</div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {organization.taxonomies.length > 2 && (
                                    <div className="d-flex w-100 flex-md-row flex-column justify-content-between gap-2 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                            onClick={handleTaxonomyPrev}
                                            disabled={taxonomyPage === 0}
                                        >
                                            Prev
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                            onClick={handleTaxonomyNext}
                                            disabled={(taxonomyPage + 1) * 2 >= organization.taxonomies.length}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-muted mb-0">No taxonomy records available.</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <MdChecklist size={24} className="hc-text-primary" />
                            <h5 className="fw-bold mb-0 hc-text-primary">Organization Reports</h5>
                        </div>
                        <div className="row g-3">
                            {detailLoading ? (
                                [1, 2].map((item) => (
                                    <div key={item} className="col-12">
                                        <div className="rounded-4 border p-3 bg-white shadow-sm h-100">
                                            <div className="skeleton mb-2 rounded-3" style={{ height: 16, width: '35%' }} />
                                            <div className="skeleton mb-2 rounded-3 w-50" style={{ height: 14 }} />
                                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 12, width: '30%', borderRadius: 8 }} />
                                        </div>
                                    </div>
                                ))
                            ) : reports.length > 0 ? (
                                <>
                                    {reports.slice(reportPage * 2, reportPage * 2 + 2).map((report) => (
                                        <div key={report.id} className="col-12">
                                            <div className="rounded-4 border p-3 bg-white shadow-sm h-100">
                                                <div className="d-flex align-items-start justify-content-between gap-3">
                                                    <div>
                                                        <div className="fw-semibold mb-1">{report.title || 'Untitled report'}</div>
                                                        <div className="small text-secondary">{report.value || '-'}</div>
                                                    </div>
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={{
                                                            backgroundColor:
                                                                STATUS_STYLE[report.status_name]?.backgroundColor || 'var(--hc-status-neutral-bg)',
                                                            color: STATUS_STYLE[report.status_name]?.color || 'var(--hc-table-footer-text)',
                                                        }}
                                                    >
                                                        {report.status_name || 'Active'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {reports.length > 2 && (
                                        <div className="col-12 w-100">
                                            <div className="d-flex flex-md-row flex-column justify-content-between gap-2 mt-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm hc-btn-prev px-5 rounded-3 py-2 shadow"
                                                    onClick={handleReportPrev}
                                                    disabled={reportPage === 0}
                                                >
                                                    Prev
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm hc-btn-next border-0 px-5 py-2 rounded-3 shadow"
                                                    onClick={handleReportNext}
                                                    disabled={(reportPage + 1) * 2 >= reports.length}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="fw-semibold text-dark text-center">No report rules configured</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-12">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <MdFilePresent size={24} className="hc-text-primary" />
                                <div>
                                    <h5 className="fw-bold mb-1 hc-text-primary">Organization Documents</h5>
                                    <p className="small text-muted mb-0">Download or inspect documents linked to this organization.</p>
                                </div>
                            </div>
                            <span className="badge rounded-pill bg-primary-subtle hc-text-primary fw-semibold">
                                {documents.length} files
                            </span>
                        </div>

                        {docsLoading ? (
                            <div className="row g-3">
                                {[1, 2, 3].map((item) => (
                                    <div key={item} className="col-12">
                                        <div className="p-4 rounded-4 shadow-sm border border-light bg-light-subtle">
                                            <div className="row g-3 align-items-center">
                                                <div className="col-auto">
                                                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 32, height: 32, borderRadius: 8 }} />
                                                </div>
                                                <div className="col">
                                                    <div className="skeleton mb-2 rounded-3 w-50" style={{ height: 18 }} />
                                                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: '30%', height: 14, borderRadius: 8 }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : documents.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <p className="mb-0">No documents have been uploaded for this organization yet.</p>
                            </div>
                        ) : (
                            <>
                                {(missingDocuments.length > 0 || notApplicableDocuments.length > 0) ? (
                                    <div className="row g-3 mb-3">
                                        {missingDocuments.length > 0 && (
                                            <div className="col-12 col-md-6">
                                                <div className="p-4 rounded-4 border bg-light-subtle h-100">
                                                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
                                                        <div>
                                                            <h6 className="fw-semibold m-0">Missing Documents</h6>
                                                            <p className="small text-muted mb-0">Missing document types for this organization.</p>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {missingDocuments.map((missingDoc, index) => (
                                                            <span
                                                                key={missingDoc?.id ?? missingDoc?.title ?? missingDoc?.name ?? index}
                                                                className="badge rounded-pill bg-danger-subtle text-danger"
                                                            >
                                                                {missingDoc?.title ?? missingDoc?.name ?? missingDoc?.label ?? String(missingDoc)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {notApplicableDocuments.length > 0 && (
                                            <div className="col-12 col-md-6">
                                                <div className="p-4 rounded-4 border bg-light-subtle h-100">
                                                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3">
                                                        <div>
                                                            <h6 className="fw-semibold m-0">Not Applicable</h6>
                                                            <p className="small text-muted mb-0">Documents marked as not applicable.</p>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {notApplicableDocuments.map((doc) => (
                                                            <span
                                                                key={typeof doc === 'string' ? doc : doc.id ?? doc.title}
                                                                className="badge rounded-pill bg-secondary-subtle text-secondary"
                                                            >
                                                                {typeof doc === 'string' ? doc : doc.title}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                                <div className="row g-3">
                                    {documents.map((document) => {
                                        const isExpanded = activeDocumentId === document.id;
                                        return (
                                            <div key={document.id} className="col-12">
                                                <div
                                                    className={`p-4 rounded-4 shadow-sm border ${isExpanded ? 'border-primary' : 'border-light'
                                                        }`}
                                                >
                                                    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
                                                        <div className="flex-grow-1">
                                                            <h6 className="fw-semibold mb-1">{document.title} {typeof document.expire_in_days === 'number' ? (
                                                                    <span
                                                                        className={`badge rounded-pill ${document.expire_in_days > 0 ? 'bg-warning-subtle text-warning' : document.expire_in_days < 0 ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning'}`}
                                                                    >
                                                                        {document.expire_in_days > 0
                                                                            ? `Expires in ${document.expire_in_days} day${document.expire_in_days === 1 ? '' : 's'}`
                                                                            : document.expire_in_days < 0
                                                                                ? `Expired ${Math.abs(document.expire_in_days)} day${Math.abs(document.expire_in_days) === 1 ? '' : 's'} before`
                                                                                : 'Expires today'}
                                                                    </span>
                                                                ) : null}</h6>
                                                            <div className="small text-secondary d-flex flex-wrap gap-2">
                                                                <span className="text-break" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                    {document.file_name || 'Document file'}
                                                                </span>
                                                                <span
                                                                    className="badge rounded-pill"
                                                                    style={{
                                                                        backgroundColor:
                                                                            STATUS_STYLE[document.status_name]?.backgroundColor || 'var(--hc-status-neutral-bg)',
                                                                        color: STATUS_STYLE[document.status_name]?.color || 'var(--hc-table-footer-text)',
                                                                    }}
                                                                >
                                                                    {document.status_name}
                                                                </span>
                                                                
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end justify-content-center align-items-stretch mt-3 mt-sm-0">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary px-3 rounded-1 py-1 shadow w-100 flex-grow-1 flex-sm-grow-0"
                                                                onClick={() => handleToggleDocument(document.id)}
                                                            >
                                                                {isExpanded ? 'Hide details' : 'View details'}
                                                            </button>
                                                            <div className="w-100">
                                                                <FileActions
                                                                    fileId={document.file_id}
                                                                    fileName={document.file_name}
                                                                    className="w-100"
                                                                    onPreview={(token, fid) => fetchOrganizationDocument(token, fid, document.file_name)}
                                                                    onDownload={(token, fid) => fetchOrganizationDocument(token, fid, document.file_name)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {isExpanded ? (
                                                        <div className="mt-4 border-top pt-3">
                                                            <div className="row g-3">
                                                                <div className="col-md-4">
                                                                    <div className="small text-muted mb-1">Uploaded</div>
                                                                    <div className="fw-semibold text-dark">{formatDocumentDate(document.created_on)}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <div className="small text-muted mb-1">Effective</div>
                                                                    <div className="fw-semibold text-dark">{formatDocumentDate(document.effective_start_date)}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <div className="small text-muted mb-1">Termination</div>
                                                                    <div className="fw-semibold text-dark">{formatDocumentDate(document.termination_date)}</div>
                                                                </div>
                                                            </div>
                                                            {document.metadata?.length ? (
                                                                <div className="mt-3">
                                                                    <div className="small text-muted mb-2">Metadata</div>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {document.metadata.map((meta, index) => (
                                                                            <span key={index} className="badge bg-light text-dark border rounded-pill small">
                                                                                {meta.key}: {meta.value}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4 align-items-stretch">
                <div className="col-12 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <MdBook size={24} className="hc-text-primary" />
                            <h5 className="fw-bold mb-0 hc-text-primary">Standard Operating Procedure</h5>
                        </div>
                        {detailLoading ? (
                            <div className="row g-3">
                                {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                                    <div key={item} className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="skeleton mb-2 rounded-3 w-50" style={{ height: 16 }} />
                                            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 18, width: '80%', borderRadius: 8 }} />
                                        </div>
                                    </div>
                                ))}
                                <div className="col-12">
                                    <div className="rounded-4 border p-3 bg-light-subtle">
                                        <div className="skeleton mb-2 rounded-3" style={{ height: 16, width: '30%' }} />
                                        <div className="skeleton position-relative overflow-hidden rounded-2 w-100" style={{ height: 60, borderRadius: 8 }} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Claim Scrubbing</div>
                                            <div className="fw-semibold text-dark">{sop?.claim_scrubbing ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">EVB</div>
                                            <div className="fw-semibold text-dark">{sop?.evb ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Claim Transfer</div>
                                            <div className="fw-semibold text-dark">{sop?.claim_transfer ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Clearing House Rejection Mgmt</div>
                                            <div className="fw-semibold text-dark">{sop?.clearing_house_rejection_mgmt ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Payment Posting</div>
                                            <div className="fw-semibold text-dark">{sop?.payment_posting ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Co-pay Posting</div>
                                            <div className="fw-semibold text-dark">{sop?.co_pay_posting ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Denial Management</div>
                                            <div className="fw-semibold text-dark">{sop?.denial_management ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Frequency</div>
                                            <div className="fw-semibold text-dark">{sop?.frequency ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Medical Coding</div>
                                            <div className="fw-semibold text-dark">{sop?.medical_coding ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Medical Coding Audit</div>
                                            <div className="fw-semibold text-dark">{sop?.medical_coding_audit ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">AR Follow-up</div>
                                            <div className="fw-semibold text-dark">{sop?.ar_follow_up ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Patient Statement</div>
                                            <div className="fw-semibold text-dark">{sop?.patient_statement ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Patient Support Toll-free Number</div>
                                            <div className="fw-semibold text-dark">{sop?.patient_support_toll_number ?? '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="rounded-4 border p-3 h-100 bg-light-subtle">
                                            <div className="small hc-text-primary fw-semibold mb-1">Paper Correspondence</div>
                                            <div className="fw-semibold text-dark">{sop?.paper_correspondence ?? '-'}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row g-3 mt-3">
                                    <div className="col-12 col-lg-6">
                                        <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                            <div className="small hc-text-primary fw-semibold mb-1">Co-pay Posting Notes</div>
                                            <div className="text-secondary" style={{ minHeight: 70 }}>
                                                {sop?.co_pay_posting_notes || 'No additional notes available.'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-lg-6">
                                        <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                            <div className="small hc-text-primary fw-semibold mb-1">AR Follow-up Notes</div>
                                            <div className="text-secondary" style={{ minHeight: 70 }}>
                                                {sop?.ar_follow_up_notes || 'No additional notes available.'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {orgMembers.length > 0 && (
                    <div className="col-12 d-flex">
                        <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <MdGroups size={24} className="hc-text-primary" />
                                <div>
                                    <h5 className="fw-bold mb-0 hc-text-primary">Organization Members</h5>
                                    <p className="small text-muted mb-0">Members responsible for handling this organization.</p>
                                </div>
                            </div>
                            <div className="row g-3">
                                {orgMembers.map((member) => (
                                    <div key={member.id} className="col-md-6 col-xl-4">
                                        <div className="rounded-4 border p-3 bg-light-subtle h-100 shadow-sm">
                                            <div className="fw-semibold mb-2">{member.name || '-'}</div>
                                            <div className="small text-secondary mb-2">{member.role_name || member.roleName || '-'}</div>
                                            <div className="d-flex align-items-center gap-2 small text-muted">
                                                <MdEmail size={16} />
                                                <span>{member.email || '-'}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                <div className="col-12 d-flex">
                    <div className="p-4 p-lg-5 hc-surface hc-card-primary h-100 w-100">
                        <h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
                            <MdPerson size={24} className="hc-text-primary" /> Audit Info
                        </h5>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Created By</div>
                                    <div className="fw-semibold text-dark">{organization.created_by || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Modified By</div>
                                    <div className="fw-semibold text-dark">{organization.modified_by || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Created On</div>
                                    <div className="fw-semibold text-dark">{createdOn}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="rounded-4 border p-3 bg-light-subtle h-100">
                                    <div className="small hc-text-primary fw-semibold mb-1">Modified On</div>
                                    <div className="fw-semibold text-dark">{modifiedOn}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default OrganizationView;